import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NumberOrNull, StringOrNull } from '../entity/types';
import * as messApi from 'api/MessApi';
import {
  conversationAddMessages,
  conversationAddSingle,
  setLastReadTime,
  setConversationData as conversationSetConversationData,
  IConversation,
} from 'store/conversation/conversationSlice';
import { IEvent } from '../tasks/tasksType';
import { RootState, TThunkApi } from '../store';

export interface ISimpleMessageContent {
  type: string;
  text: string;
}

export interface IFileMessageContent {
  url: string;
  type: string;
  text: string;
  width: number;
  height: number;
  fileSize: number;
  fileType: string;
  fileName: string;
  contentId: string;
  thumbnailId: string;
  thumbnailUrl: string;
  expirationDate: Date;
}

export interface IMessageView {
  archived: boolean;
  content: ISimpleMessageContent | IFileMessageContent | IEvent | string;
  conversationId: number;
  id: number;
  ignoreNotify: number[] | null;
  readThisMessage: number[];
  senderId: number;
  sendStatus: 'waiting' | 'sent' | 'rejected' | 'draft' | null;
  sendTime: number;
  title: StringOrNull;
  visibleTo: NumberOrNull;
  uploading?: boolean;
}

export interface IMessageViewServer {
  content: string;
  conversationId?: number;
  sendStatus: 'waiting' | 'sent' | 'rejected' | 'draft' | null;
  senderId?: number;
}

export interface IMessage {
  id: number;
  content: string;
  uploading: boolean;
  archived: boolean;
  readThisMessage: Array<number>;
  sendTime: number;
  conversationId: number;
  senderId: number;
}

export interface IMessageState {
  data: {
    [key: number]: IMessageView;
  };
}

const initialState: IMessageState = {
  data: {},
};

// Helpers
function messageManipulate(message: IMessageView) {
  const clonedMessage = { ...message };
  clonedMessage.content = JSON.parse(clonedMessage.content as string);
  return clonedMessage;
}

// Reducers
const addMessages = createAsyncThunk(
  'messages/addMessages',
  async (
    { messages }: { messages: Array<IMessageView> },
    ThunkApi: TThunkApi
  ) => {
    ThunkApi.dispatch(addMessagesAction({ messages }));
    ThunkApi.dispatch(conversationAddMessages({ messages }));
  }
);
function addMessagesHandler(
  state: IMessageState,
  action: PayloadAction<{ messages: Array<IMessageView> }>
) {
  action.payload.messages.forEach((message) => {
    state.data[message.id] = messageManipulate(message);
  });
}

const addSingleMessage = createAsyncThunk(
  'messages/addSingleMessage',
  async (
    { message, userId }: { message: IMessageView; userId?: NumberOrNull },
    ThunkApi: TThunkApi
  ) => {
    ThunkApi.dispatch(addSingleMessageAction({ message, userId }));
    ThunkApi.dispatch(conversationAddSingle({ message, userId }));
  }
);

function addSingleMessageHandler(
  state: IMessageState,
  action: PayloadAction<{ message: IMessageView; userId?: number | null }>
) {
  state.data[action.payload.message.id] = messageManipulate(
    action.payload.message
  );
}

function messageUploadedHandler(
  state: IMessageState,
  action: PayloadAction<{ messageId: number }>
) {
  state.data[action.payload.messageId].uploading = false;
}

function removeSingleMessageHandler(
  state: IMessageState,
  action: PayloadAction<{ messageId: number }>
) {
  delete state.data[action.payload.messageId];
}

function archiveMessagesHandler(
  state: IMessageState,
  action: PayloadAction<{ messagesIds: Array<number>; archive: boolean }>
) {
  action.payload.messagesIds.forEach((msgId) => {
    state.data[msgId].archived = action.payload.archive;
  });
}

function markAsReadHandler(
  state: IMessageState,
  action: PayloadAction<{ messagesIds: Array<number>; userId: number }>
) {
  action.payload.messagesIds.forEach((msgId) => {
    state.data[msgId].readThisMessage.push(action.payload.userId);
  });
}

const setConversationData = createAsyncThunk(
  'messages/setConversationData',
  async (
    { conversation }: { conversation: { messages: Array<IMessageView> } },
    ThunkApi: TThunkApi
  ) => {
    ThunkApi.dispatch(setConversationDataAction({ conversation }));
    ThunkApi.dispatch(
      conversationSetConversationData({
        conversation: conversation as IConversation,
      })
    );
  }
);

function setConversationDataHandler(
  state: IMessageState,
  action: PayloadAction<{ conversation: { messages: Array<IMessageView> } }>
) {
  action.payload.conversation.messages.forEach((message) => {
    state.data[message.id] = messageManipulate(message);
  });
}

// Thunks
export const uploadFileFn = async (
  {
    url,
    messageId,
    file,
    onProgressCallback,
  }: {
    url: string;
    messageId: number;
    file: unknown;
    onProgressCallback: (progress: unknown) => void;
  },
  ThunkApi: TThunkApi
) => {
  return messApi
    .uploadMessageFile(url, file, onProgressCallback)
    .then(() => {
      ThunkApi.dispatch(messageUploaded({ messageId }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const uploadFile = createAsyncThunk('messages/uploadFile', uploadFileFn);

export const sendMessageFn = async (
  {
    msg,
    parseContent = false,
    file,
    userId = null,
    conversationToken,
    onProgressCallback,
  }: {
    msg: IMessageViewServer;
    parseContent: boolean;
    file: unknown;
    userId: number | null;
    conversationToken?: string;
    onProgressCallback: (progress: unknown) => void;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return messApi
    .sendMessage(store, msg, parseContent, conversationToken)
    .then((res) => {
      const message = res.data;
      if (file) {
        message.uploading = true;
        ThunkApi.dispatch(addSingleMessage({ message, userId }));
        const content = JSON.parse(message.content);
        return ThunkApi.dispatch(
          uploadFile({
            url: content.url,
            messageId: message.id,
            file,
            onProgressCallback,
          })
        )
          .unwrap()
          .then(() => {
            return ThunkApi.dispatch(
              uploadStatus({ status: true, messageId: message.id })
            )
              .unwrap()
              .then(
                () => {
                  return Promise.resolve();
                },
                (err: any) => {
                  ThunkApi.dispatch(
                    removeSingleMessage({ messageId: message.id })
                  );
                  return ThunkApi.rejectWithValue(err) as any;
                }
              );
          })
          .catch((err: unknown) => {
            return ThunkApi.dispatch(
              uploadStatus({ status: false, messageId: message.id })
            )
              .unwrap()
              .then(
                () => {
                  return ThunkApi.rejectWithValue(err) as any;
                },
                (err: any) => {
                  return ThunkApi.rejectWithValue(err) as any;
                }
              );
          });
      } else {
        ThunkApi.dispatch(addSingleMessage({ message, userId }));
        return Promise.resolve();
      }
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const sendMessage = createAsyncThunk('messages/sendMessage', sendMessageFn);

export const uploadStatusFn = async (
  {
    status,
    messageId,
    userId,
    conversationToken,
  }: {
    status: boolean;
    messageId: number;
    userId?: number;
    conversationToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (status) {
    return messApi
      .messageUploaded(store, messageId, conversationToken)
      .then((res) => {
        ThunkApi.dispatch(addSingleMessage({ message: res.data, userId }));
        return Promise.resolve({ message: res.data });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
  return messApi
    .messageUploadCanceled(store, messageId)
    .then(() => Promise.resolve())
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const uploadStatus = createAsyncThunk('messages/uploadStatus', uploadStatusFn);

export const requireMessagesBeforeMessageFn = async (
  {
    conversationId,
    messageId,
    conversationToken = null,
    count = 50,
    archived = false,
  }: {
    conversationId: number;
    messageId: number;
    conversationToken?: string | null;
    count?: number;
    archived?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return messApi
    .getMyMessagesBeforeMessage(
      store,
      conversationId,
      conversationToken,
      messageId,
      count,
      archived
    )
    .then((res) => {
      ThunkApi.dispatch(addMessages({ messages: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireMessagesBeforeMessage = createAsyncThunk(
  'messages/requireMessagesBeforeMessage',
  requireMessagesBeforeMessageFn
);

export const requireMessagesAfterMessageFn = async (
  {
    conversationId,
    messageId,
    conversationToken = null,
    count = null,
    archived = false,
  }: {
    conversationId: number;
    messageId: number;
    conversationToken: string | null;
    count?: number | null;
    archived?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return messApi
    .getMyMessagesAfterMessage(
      store,
      conversationId,
      conversationToken,
      messageId,
      count,
      archived
    )
    .then((res) => {
      ThunkApi.dispatch(addMessages({ messages: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireMessagesAfterMessage = createAsyncThunk(
  'messages/requireMessagesAfterMessage',
  requireMessagesAfterMessageFn
);

export const requireNewMessagesFn = async (
  {
    conversationId,
    messageId,
    conversationToken = null,
  }: {
    conversationId: number;
    messageId: number;
    conversationToken: string | null;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const msg = store.messages.data[Number(messageId)];

  if (!msg) {
    const latestMsgId =
      store.conversations.data[conversationId].newestMessage.id;
    if (latestMsgId) {
      return ThunkApi.dispatch(
        requireMessagesAfterMessage({
          conversationId,
          messageId: latestMsgId,
          conversationToken,
        })
      )
        .unwrap()
        .then(() => Promise.resolve())
        .catch((err: any) => {
          return ThunkApi.rejectWithValue(err) as any;
        });
    }
  }

  return Promise.resolve();
};
const requireNewMessages = createAsyncThunk(
  'messages/requireNewMessages',
  requireNewMessagesFn
);

export const archiveMessagesFn = async (
  { messagesIds, archive }: { messagesIds: Array<number>; archive: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  ThunkApi.dispatch(archiveMessagesAction({ messagesIds, archive }));
  return messApi.archiveMessages(store, messagesIds, archive).then(
    () => Promise.resolve(),
    (err) => {
      ThunkApi.dispatch(
        archiveMessagesAction({ messagesIds, archive: !archive })
      );
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const archiveMessages = createAsyncThunk(
  'messages/archiveMessages',
  archiveMessagesFn
);

export const markMessagesAsReadFn = async (
  { messagesIds }: { messagesIds: Array<number> },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return messApi
    .markMessagesAsRead(store, messagesIds)
    .then(() => {
      const userId = Number(store.thisUser.userId);
      ThunkApi.dispatch(markAsReadAction({ messagesIds, userId }));
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const markMessagesAsRead = createAsyncThunk(
  'messages/markMessagesAsRead',
  markMessagesAsReadFn
);

export const markAsReadFn = async (
  { conversationId }: { conversationId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const lastMsgId = store.conversations.data[conversationId].messages.map(
    (msg) => msg.id
  );

  if (lastMsgId) {
    return messApi
      .markMessagesAsRead(store, lastMsgId)
      .then(() => {
        ThunkApi.dispatch(
          setLastReadTime({
            conversationId: conversationId,
            timestamp: Date.now(),
          })
        );
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
};
const markAsRead = createAsyncThunk('messages/markAsRead', markAsReadFn);

const messagesSlice = createSlice({
  name: 'messages',
  initialState: initialState,
  reducers: {
    addMessagesAction: addMessagesHandler,
    addSingleMessageAction: addSingleMessageHandler,
    messageUploaded: messageUploadedHandler,
    removeSingleMessage: removeSingleMessageHandler,
    archiveMessagesAction: archiveMessagesHandler,
    markAsReadAction: markAsReadHandler,
    setConversationDataAction: setConversationDataHandler,
  },
});

export const {
  addMessagesAction,
  addSingleMessageAction,
  messageUploaded,
  removeSingleMessage,
  archiveMessagesAction,
  markAsReadAction,
  setConversationDataAction,
} = messagesSlice.actions;

export {
  uploadFile,
  sendMessage,
  uploadStatus,
  requireMessagesBeforeMessage,
  requireMessagesAfterMessage,
  requireNewMessages,
  archiveMessages,
  markMessagesAsRead,
  markAsRead,
  addMessages,
  addSingleMessage,
  setConversationData,
};

export default messagesSlice.reducer;
